import type { RefObject } from 'react';

export type MxpModuleIdV2 =
  | 'mxp365-announcements'
  | 'mxp365-board-activities'
  | 'mxp365-chat'
  | 'mxp365-daily-by-id'
  | 'mxp365-deck-plan'
  | 'mxp365-favorites'
  | 'mxp365-fitness'
  | 'mxp365-hair-dresser'
  | 'mxp365-makeup'
  | 'mxp365-messages'
  | 'mxp365-my-calendar'
  | 'mxp365-opening-hours'
  | 'mxp365-packages'
  | 'mxp365-packages-by-id'
  | 'mxp365-packages-mementos'
  | 'mxp365-port-info'
  | 'mxp365-questionnaires-by-id'
  | 'mxp365-restaurants'
  | 'mxp365-restaurants-by-id'
  | 'mxp365-safety-instructions'
  | 'mxp365-shorex'
  | 'mxp365-spa'
  | 'mxp365-spa-balcony-cabin'
  | 'mxp365-spa-by-id'
  | 'mxp365-tour-program-itinerary'
  | 'mxp365-venues'
  | 'mxp365-venues-by-id'
  | 'mxp365-weather';

export const PARAMETERIZED_MODULES: ReadonlySet<MxpModuleIdV2> =
  new Set<MxpModuleIdV2>([
    'mxp365-daily-by-id',
    'mxp365-packages-by-id',
    'mxp365-questionnaires-by-id',
    'mxp365-restaurants-by-id',
    'mxp365-spa-by-id',
    'mxp365-tour-program-itinerary',
    'mxp365-venues-by-id',
  ]);

export type ModuleReadyStatus = 'success' | 'no-op' | 'error';

export type MxpV2ErrorCode =
  | 'NAVIGATION_TIMEOUT'
  | 'NAVIGATION_FAILED'
  | 'WEBSOCKET_DISCONNECTED'
  | 'MODULE_NOT_FOUND'
  | 'CLIENT_TIMEOUT';

export type NavigateToV2Payload = {
  mxpModule: MxpModuleIdV2;
  mxpParameter: string | null;
  requestId: string;
};

export type ModuleReadyV2Payload = {
  status: ModuleReadyStatus;
  currentUrl: string;
  requestId: string;
  errorCode: MxpV2ErrorCode | null;
  errorMessage: string | null;
  timestamp: number;
};

export type NavigateToV2Message = {
  action: 'navigateToV2';
  payload: NavigateToV2Payload;
};

export type ModuleReadyV2Message = {
  action: 'moduleReadyV2';
  payload: ModuleReadyV2Payload;
};

export type MxpV2AuxShareHandler = (args: {
  url: string;
  title?: string;
  text?: string;
}) => void;
export type MxpV2AuxFavoriteAddedHandler = () => void;
export type MxpV2AuxHeightChangeHandler = (args: { height: number }) => void;
export type MxpV2AuxNavigateHandler = (args: {
  allowedNavigateBack: boolean;
}) => void;
export type MxpV2AuxTitleChangeHandler = (args: {
  locationUri: string;
}) => void;
export type MxpV2AuxJwtLoginErrorHandler = (args: {
  payload?: Record<string, string | undefined>;
}) => void;

export type MxpV2State = {
  ready: boolean;
  currentUrl: string | null;
  lastEvent: ModuleReadyV2Payload | null;
  navigating: boolean;
};

export type UseMxpIframeOptions = {
  src: string;
  targetOrigin: string;
  timeoutMs?: number;
  onEvent?: (payload: ModuleReadyV2Payload) => void;
  onReady?: (payload: ModuleReadyV2Payload) => void;
  onShare?: MxpV2AuxShareHandler;
  onFavoriteAdded?: MxpV2AuxFavoriteAddedHandler;
  onHeightChange?: MxpV2AuxHeightChangeHandler;
  onNavigate?: MxpV2AuxNavigateHandler;
  onTitleChange?: MxpV2AuxTitleChangeHandler;
  onJwtLoginError?: MxpV2AuxJwtLoginErrorHandler;
};

export type UseMxpIframeReturn = {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  state: MxpV2State;
  navigate: (
    module: MxpModuleIdV2,
    parameter?: string | null,
  ) => Promise<ModuleReadyV2Payload>;
  reload: () => void;
  postMessage: (
    message: NavigateToV2Message | { action: string; payload?: unknown },
  ) => void;
};
